import React, { useContext, useState } from 'react';
import DialogBox from '../../../Common/Layout/DialogBox';
import {
  IAwardType,
  IHistoryAward,
  useCreateAwardMutation,
  useGetAwardTypesQuery,
  useUpdateAwardMutation
} from '../../../../generated/graphql';
import { Box } from '@mui/material';
import EditButton from '../../../Common/Buttons/EditButton';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { canUserEdit } from '../../../../utils/rightUtils';
import { Colors } from '../../../../utils/theme';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';
import { parseDate } from '../../../../utils/clientUtils';
import AutocompleteComponent from '../../../Common/Fields/AutocompleteComponent';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';

interface IProps {
  stockId: string;
  handleClose: (saved: boolean, isNew?: boolean, awardInput?: IHistoryAward) => void;
  awardInput: IHistoryAward | null;
}

type IErrors = {
  type: string | null;
  dateStart: string | null;
};

const defaultErrorsObj: IErrors = {
  type: null,
  dateStart: null
};

const errorTexts = {
  date: 'Date is required!',
  type: 'Award Type is required!'
};

const AwardForm: React.FunctionComponent<IProps> = ({
  stockId,
  awardInput,
  handleClose
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [awardType, setAwardType] = useState(awardInput?.type || null);
  const [dateStart, setDateStart] = useState<Date | null>(
    awardInput ? parseDate(awardInput.date) : new Date()
  );
  const [editable, setEditable] = useState(awardInput === null);
  const awardTypes = useGetAwardTypesQuery();
  const [createAward, createAwardResult] = useCreateAwardMutation();
  const [updateAward, updateAwardResult] = useUpdateAwardMutation();
  const canEdit = canUserEdit(state.user, Types.Stock);

  const handleAwardTypeChange = (_: unknown, newValue: React.SetStateAction<IAwardType | null>) => {
    setFormErrors({ ...formErrors, type: newValue ? null : errorTexts.type });
    setAwardType(newValue);
  };

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFormErrors({ ...formErrors, dateStart: null });
      }
    } else {
      setFormErrors({ ...formErrors, dateStart: errorTexts.date });
    }
    setDateStart(input);
  };

  const handleCancel = () => {
    if (editable && awardInput !== null) {
      setAwardType(awardInput?.type || null);
      setDateStart(parseDate(awardInput.date));
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      type: null,
      dateStart: null
    };
    if (!dateStart) {
      hasErrors = true;
      errors.dateStart = errorTexts.date;
    }
    if (!awardType) {
      hasErrors = true;
      errors.type = errorTexts.type;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(awardInput === null ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createAward({
        variables: {
          stock_id: stockId,
          input: {
            type_id: awardType?.id as string,
            date: (dateStart as Date).toString()
          }
        },
        update(_, { data }) {
          handleClose(true, true, data?.result as IHistoryAward);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Award created'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    } else {
      updateAward({
        variables: {
          id: awardInput?.id as string,
          input: {
            type_id: awardType?.id as string,
            date: (dateStart as Date).toString()
          }
        },
        update(_, { data }) {
          handleClose(true, false, data?.result as IHistoryAward);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Award updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="dividend-dialog"
      width={520}
      title={awardInput ? 'Award Details' : 'Create Award'}
      onClose={handleClose}
      childrenSx={{ px: '40px', py: '20px' }}
    >
      {awardInput && (
        <Box sx={{ textAlign: 'right', width: '100%' }}>
          <EditButton
            id="award-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box display="flex" gap="15px" sx={{ my: '20px' }}>
        <AutocompleteComponent
          id="award-type-autocomplete"
          options={(awardTypes.data?.result as never[]) || []}
          getOptionLabel={(option: IAwardType) => option.name as string}
          values={awardType}
          onChange={handleAwardTypeChange}
          errorMsg={formErrors.type}
          label="Award Type"
          disabled={createAwardResult.loading || updateAwardResult.loading || !editable}
        />
        <Box sx={{ width: '100%' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <EpraDatePicker
              id="award-date-picker"
              label="Date"
              value={dateStart}
              disabled={createAwardResult.loading || updateAwardResult.loading || !editable}
              onChange={(d) => handleDateChange(d)}
              errorMsg={formErrors.dateStart}
            />
          </LocalizationProvider>
        </Box>
      </Box>
      <Box
        className="centralize"
        sx={{
          gridColumn: 'span 3',
          marginTop: '50px',
          paddingBottom: '45px'
        }}
      >
        <CancelButton
          id="cancel-award-button"
          onClick={handleCancel}
          disabled={createAwardResult.loading || updateAwardResult.loading}
          text={awardInput === null || editable ? 'Cancel' : 'Close'}
        />
        <SaveButton
          id="save-award-button"
          data-testid="save-award-button"
          onClick={handleSubmit}
          disabled={createAwardResult.loading || updateAwardResult.loading}
          text={awardInput === null ? 'Create' : 'Save'}
        />
      </Box>
    </DialogBox>
  );
};

export default AwardForm;
